import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Badge, EmptyState, Spinner } from "@/components/ui";
import Button from "@/components/ui/Button";
import { useAppStore } from "@/store/appStore";
import { useAuthStore } from "@/store/authStore";
import clsx from "clsx";

const DIFF_LABELS = ["", "⭐ Leicht", "⭐⭐ Mittel", "⭐⭐⭐ Schwer", "⭐⭐⭐⭐ Expert", "⭐⭐⭐⭐⭐ Legende"];
const CAT_COLORS  = { Mathe: "purple", Deutsch: "cyan", Sachkunde: "green", Logik: "gold", Geografie: "green", Englisch: "cyan", Geschichte: "pink", Informatik: "purple" };

/* ── Timer bar ──────────────────────────────────────────────────────────────── */
function TimerBar({ timer, limit = 30 }) {
  const pct     = Math.max(0, (1 - timer / limit) * 100);
  const urgent  = timer > limit * 0.7;
  const color   = urgent ? "#ef4444" : timer > limit * 0.4 ? "#fbbf24" : "#22c55e";

  return (
    <div className="relative h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
      <div
        className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000"
        style={{
          width:     `${pct}%`,
          background: color,
          boxShadow: `0 0 10px ${color}80`,
          transition: "width 1s linear, background 0.5s ease",
        }}
      />
    </div>
  );
}

/* ── Option button letter badge ─────────────────────────────────────────────── */
const OPTION_LETTERS = ["A", "B", "C", "D"];

/* ── Quiz List ──────────────────────────────────────────────────────────────── */
function QuizList({ onStart }) {
  const [cat, setCat] = useState("alle");
  const { data, isLoading } = useQuery({
    queryKey: ["quizzes"],
    queryFn:  () => api.get("/quizzes").then((r) => r.data),
  });

  const quizzes = data?.quizzes || [];
  const cats    = ["alle", ...new Set(quizzes.map((q) => q.category))];
  const shown   = cat === "alle" ? quizzes : quizzes.filter((q) => q.category === cat);

  if (isLoading) return (
    <div className="flex justify-center pt-16">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-2 border-t-violet-500 border-r-cyan-500 border-b-transparent border-l-transparent animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={clsx(
              "flex-shrink-0 px-4 py-1.5 rounded-xl text-xs font-bold border transition-all duration-200",
              c === cat
                ? "bg-violet-600 border-violet-500 text-white shadow-flow"
                : "bg-nexus-s2 border-nexus-border text-gray-400 hover:text-white hover:border-violet-500/30"
            )}
          >
            {c === "alle" ? "✦ Alle" : c}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon="📚" title="Keine Quizze" desc="Bald kommen neue Quizze!" />
      ) : (
        <div className="space-y-2.5">
          {shown.map((q) => {
            const done    = !!q.bestAttempt;
            const perfect = done && q.bestAttempt.score === q.bestAttempt.totalQuestions;
            return (
              <Card
                key={q.id}
                hover
                onClick={() => onStart(q)}
                className={clsx(
                  "p-4",
                  perfect && "border-yellow-500/35",
                  done && !perfect && "border-green-500/25",
                )}
              >
                <div className="flex items-center gap-3">
                  {/* Emoji box */}
                  <div
                    className="w-12 h-12 rounded-xl bg-nexus-s2 flex items-center justify-center text-2xl flex-shrink-0 border border-nexus-border"
                    style={perfect ? { boxShadow: "0 0 12px rgba(251,191,36,0.3)", borderColor: "rgba(251,191,36,0.3)" } : undefined}
                  >
                    {q.emoji}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-black text-sm">{q.title}</h3>
                      {perfect && <span className="text-xs" style={{ filter: "drop-shadow(0 0 4px rgba(251,191,36,0.7))" }}>🏆</span>}
                      {done && !perfect && <span className="text-xs">✅</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge color={CAT_COLORS[q.category] || "purple"}>{q.category}</Badge>
                      <span className="text-[10px] text-gray-500">{DIFF_LABELS[q.difficulty]}</span>
                    </div>
                    {done && (
                      <p className="text-[10px] text-green-400 mt-1 font-bold">
                        Bestes Ergebnis: {q.bestAttempt.score}/{q.bestAttempt.totalQuestions} · +{q.bestAttempt.xpEarned}XP
                      </p>
                    )}
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-black text-violet-400">+{q.xpReward}</p>
                    <p className="text-[10px] text-gray-600">XP</p>
                    <p className="text-[10px] text-gray-600 mt-0.5">{q.questions?.length}F</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Active Quiz ────────────────────────────────────────────────────────────── */
function ActiveQuiz({ quiz, onDone, onExit }) {
  const [qIdx,     setQIdx]     = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [timer,    setTimer]    = useState(0);
  const [answers,  setAnswers]  = useState([]);
  const [score,    setScore]    = useState(0);
  const [done,     setDone]     = useState(false);
  const showReward      = useAppStore((s) => s.showReward);
  const { refreshMe }   = useAuthStore();
  const qc              = useQueryClient();

  const submitMutation = useMutation({
    mutationFn: (payload) => api.post(`/quizzes/${quiz.id}/attempt`, payload),
    onSuccess: (res) => {
      showReward({ xp: res.data.xpEarned, coins: res.data.coinsEarned, reason: `Quiz: ${quiz.title}` });
      refreshMe();
      qc.invalidateQueries({ queryKey: ["student", "me"] });
      qc.invalidateQueries({ queryKey: ["quizzes"] });
      onDone(res.data);
    },
  });

  useEffect(() => {
    if (done || answered) return;
    const t = setInterval(() => setTimer((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [done, answered, qIdx]);

  const question = quiz.questions[qIdx];

  const handleAnswer = useCallback((idx) => {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);
    const isCorrect = idx === question.correct;
    if (isCorrect) setScore((s) => s + 1);
    setAnswers((a) => [...a, { selected: idx, timeTaken: timer }]);

    setTimeout(() => {
      if (qIdx + 1 < quiz.questions.length) {
        setQIdx((i) => i + 1);
        setSelected(null);
        setAnswered(false);
        setTimer(0);
      } else {
        setDone(true);
        const finalAnswers = [...answers, { selected: idx, timeTaken: timer }];
        submitMutation.mutate({ answers: finalAnswers, timeTaken: timer });
      }
    }, 1500);
  }, [answered, timer, qIdx, answers, quiz]);

  const opts = JSON.parse(typeof question.options === "string" ? question.options : JSON.stringify(question.options));

  /* ── Results screen ──────────────────────────────────────────────────────── */
  if (done) {
    const isPerfect  = score === quiz.questions.length;
    const isGood     = score >= quiz.questions.length * 0.6;
    const resultData = submitMutation.data?.data;

    return (
      <div className="space-y-5 animate-fade-in">
        <Card glow aurora className="p-8 text-center relative">
          <div
            className="text-7xl mb-4 inline-block animate-bounce-in"
            style={{ filter: isPerfect ? "drop-shadow(0 0 20px rgba(251,191,36,0.8))" : "drop-shadow(0 0 12px rgba(139,92,246,0.5))" }}
          >
            {isPerfect ? "🏆" : isGood ? "👍" : "💪"}
          </div>

          <h2 className={clsx("text-2xl font-black mb-2", isPerfect && "text-gradient-gold")}>
            {isPerfect ? "PERFEKT! 🔥" : isGood ? "Gut gemacht!" : "Weiter üben!"}
          </h2>

          <div
            className="inline-flex items-center gap-1 text-sm font-bold px-4 py-1.5 rounded-full border mb-6"
            style={isPerfect
              ? { color: "#fbbf24", borderColor: "rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.08)" }
              : { color: "#8b5cf6", borderColor: "rgba(139,92,246,0.4)", background: "rgba(139,92,246,0.08)" }
            }
          >
            {score}/{quiz.questions.length} richtig
          </div>

          {resultData && (
            <div className="flex justify-center gap-4 mb-6">
              <div
                className="px-6 py-4 rounded-2xl flex flex-col items-center"
                style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.3)" }}
              >
                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">XP</p>
                <p className="text-2xl font-black text-violet-400">+{resultData.xpEarned}</p>
              </div>
              <div
                className="px-6 py-4 rounded-2xl flex flex-col items-center"
                style={{ background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.3)" }}
              >
                <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">COINS</p>
                <p className="text-2xl font-black text-yellow-400">+{resultData.coinsEarned}</p>
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-center flex-wrap">
            <Button
              onClick={() => {
                setDone(false); setQIdx(0); setScore(0);
                setAnswers([]); setTimer(0); setSelected(null); setAnswered(false);
              }}
            >
              🔄 Nochmal
            </Button>
            <Button variant="secondary" onClick={onExit}>← Zurück</Button>
          </div>
        </Card>
      </div>
    );
  }

  /* ── Active question ─────────────────────────────────────────────────────── */
  return (
    <div className="space-y-4 animate-fade-in">

      {/* Header row */}
      <div className="flex items-center justify-between">
        <button
          onClick={onExit}
          className="flex items-center gap-1.5 text-gray-500 hover:text-white transition-colors text-sm font-bold"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Verlassen
        </button>
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              "flex items-center gap-1 text-xs font-black px-2.5 py-1 rounded-xl border transition-colors",
              timer > 20 ? "text-red-400 border-red-500/30 bg-red-500/08" :
              timer > 12 ? "text-yellow-400 border-yellow-500/30 bg-yellow-500/08" :
              "text-green-400 border-green-500/30 bg-green-500/08"
            )}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            {timer}s
          </div>
          <span className="text-xs font-black text-violet-400 bg-violet-500/10 px-2.5 py-1 rounded-xl border border-violet-500/20">
            {qIdx + 1}/{quiz.questions.length}
          </span>
        </div>
      </div>

      {/* Progress track */}
      <div className="space-y-1.5">
        <div className="flex gap-1">
          {quiz.questions.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1 rounded-full transition-all duration-500"
              style={{
                background: i < qIdx
                  ? "#22c55e"
                  : i === qIdx
                    ? undefined
                    : "rgba(255,255,255,0.06)",
              }}
            >
              {i === qIdx && <div className="h-full rounded-full xp-bar" />}
            </div>
          ))}
        </div>

        {/* Timer bar */}
        <TimerBar timer={timer} limit={30} />
      </div>

      {/* Score chips */}
      <div className="flex justify-center gap-3">
        <div className="flex items-center gap-1.5 text-xs font-black text-green-400 bg-green-500/08 px-3 py-1 rounded-xl border border-green-500/20">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#22c55e" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          {score} richtig
        </div>
        <div className="flex items-center gap-1.5 text-xs font-black text-red-400 bg-red-500/08 px-3 py-1 rounded-xl border border-red-500/20">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#ef4444" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          {qIdx - score} falsch
        </div>
      </div>

      {/* Question card */}
      <Card glow className="p-6 relative overflow-hidden">
        {/* Subtle emoji watermark */}
        <div className="absolute top-3 right-3 text-4xl opacity-[0.07] select-none pointer-events-none">{quiz.emoji}</div>

        <div className="relative z-10">
          <h3 className="text-lg font-black text-center mb-6 leading-snug">{question.text}</h3>

          <div className="space-y-2.5">
            {opts.map((opt, i) => {
              const isCorrect  = i === question.correct;
              const isSelected = i === selected;
              let stateClass = "";
              if (answered && isCorrect)                      stateClass = "quiz-opt-correct";
              else if (answered && isSelected && !isCorrect)  stateClass = "quiz-opt-wrong";
              else if (answered)                              stateClass = "opacity-40";

              return (
                <button
                  key={i}
                  onClick={() => handleAnswer(i)}
                  disabled={answered}
                  className={clsx(
                    "quiz-opt w-full p-3.5 rounded-xl border-2 text-left font-semibold text-sm transition-all duration-200",
                    "bg-nexus-s2 border-nexus-border",
                    !answered && "hover:border-violet-500/50 hover:bg-nexus-s3 hover:scale-[1.01] active:scale-[0.99]",
                    stateClass
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex w-7 h-7 items-center justify-center rounded-lg text-xs font-black flex-shrink-0 transition-all"
                      style={{
                        background: answered && isCorrect ? "rgba(34,197,94,0.3)" :
                                    answered && isSelected && !isCorrect ? "rgba(239,68,68,0.3)" :
                                    "rgba(139,92,246,0.15)",
                        color: answered && isCorrect ? "#22c55e" : answered && isSelected ? "#ef4444" : "#8b5cf6",
                      }}
                    >
                      {OPTION_LETTERS[i]}
                    </span>
                    <span className="flex-1">{opt}</span>
                    {answered && isCorrect  && <span className="flex-shrink-0 text-sm">✅</span>}
                    {answered && isSelected && !isCorrect && <span className="flex-shrink-0 text-sm">❌</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Explanation */}
          {answered && question.explain && (
            <div
              className="mt-4 p-3.5 rounded-xl border animate-slide-up"
              style={{ background: "rgba(6,182,212,0.06)", borderColor: "rgba(6,182,212,0.3)" }}
            >
              <p className="text-xs text-cyan-300 font-bold">
                💡 {question.explain}
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ── Main export ────────────────────────────────────────────────────────────── */
export default function Quiz() {
  const { id }  = useParams();
  const navigate = useNavigate();
  const [activeQuiz, setActiveQuiz] = useState(null);

  const { data: singleQuiz } = useQuery({
    queryKey: ["quiz", id],
    queryFn:  () => api.get(`/quizzes/${id}`).then((r) => r.data),
    enabled:  !!id && !activeQuiz,
  });

  useEffect(() => {
    if (singleQuiz?.quiz) setActiveQuiz(singleQuiz.quiz);
  }, [singleQuiz]);

  if (activeQuiz) {
    return (
      <div className="page-enter">
        <ActiveQuiz
          quiz={activeQuiz}
          onDone={() => { setActiveQuiz(null); navigate("/quiz"); }}
          onExit={() => { setActiveQuiz(null); navigate("/quiz"); }}
        />
      </div>
    );
  }

  return (
    <div className="page-enter">
      <QuizList onStart={(q) => setActiveQuiz(q)} />
    </div>
  );
}
